import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { AD_CAMPAIGN_WARMUPS } from '@/graphql/warmup-queries'

// The server answers at most this many ad campaigns in one request
export const WARMUP_BATCH_SIZE = 100

const EMPTY = Object.freeze({})

export const chunk = (items, size) => {
    const batches = []
    for (let at = 0; at < items.length; at += size) batches.push(items.slice(at, at + size))
    return batches
}

/**
 * The warm-up, holdout assignment and "warm-up not used" notice of the ad campaigns on screen, read in batches of at
 * most 100 and mapped by ad campaign id. An id the server reports as not found has no entry. Nothing is read for an
 * empty list. `refresh` reads them again; `applyWarmup` puts a warm-up that an owner action answered with into the map.
 * @param {string[]} adCampaignIds
 * @returns {{ entries: Record<string, any>, loading: boolean, refresh: () => Promise<void>, applyWarmup: (adCampaignId: string, warmup: any) => void }}
 */
export const useWarmupEntries = (adCampaignIds) => {
    const client = useApolloClient()
    const [entries, setEntries] = useState(EMPTY)
    const [loading, setLoading] = useState(false)
    // Every read and every applied answer gets a number: the latest read wins, and a read that started before an answer
    // was applied never replaces that answer
    const sequenceRef = useRef(0)
    const latestReadRef = useRef(0)
    const appliedRef = useRef({})
    const idsKey = useMemo(() => [...new Set(adCampaignIds)].join(','), [adCampaignIds])

    const refresh = useCallback(async () => {
        const ids = idsKey ? idsKey.split(',') : []
        const request = ++sequenceRef.current
        latestReadRef.current = request
        if (ids.length === 0) {
            setEntries(EMPTY)
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const answers = await Promise.all(
                chunk(ids, WARMUP_BATCH_SIZE).map((batch) =>
                    client.query({
                        query: AD_CAMPAIGN_WARMUPS,
                        variables: { adCampaignIds: batch },
                        fetchPolicy: 'no-cache',
                        errorPolicy: 'all',
                    }),
                ),
            )
            if (request !== latestReadRef.current) return
            const next = {}
            answers.forEach(({ data }) =>
                (data?.AdCampaign?.adCampaignWarmups || []).forEach((entry) => {
                    if (entry.found) next[entry.ad_campaign_id] = entry
                }),
            )
            setEntries((current) => {
                const merged = { ...next }
                Object.keys(merged).forEach((id) => {
                    // An answer applied after this read started is newer than the read
                    if (appliedRef.current[id] > request && current[id]?.warmup) {
                        merged[id] = { ...merged[id], warmup: current[id].warmup }
                    }
                })
                return merged
            })
        } catch (error) {
            // The panel is optional information: without it the Ads Manager works as before
            if (request === latestReadRef.current) setEntries(EMPTY)
        } finally {
            if (request === latestReadRef.current) setLoading(false)
        }
    }, [client, idsKey])

    useEffect(() => {
        void refresh()
    }, [refresh])

    const applyWarmup = useCallback((adCampaignId, warmup) => {
        // A read that started before this answer must not overwrite it
        appliedRef.current[adCampaignId] = ++sequenceRef.current
        setEntries((current) =>
            current[adCampaignId] ? { ...current, [adCampaignId]: { ...current[adCampaignId], warmup } } : current,
        )
    }, [])

    return { entries, loading, refresh, applyWarmup }
}
